/* eslint-disable @typescript-eslint/no-explicit-any */
// TODO: This file was automatically migrated to TypeScript by `dev typescript migrate`.
//  The eslint disable comment above should be removed and the resulting errors fixed.
import classNames from 'classnames';
import React from 'react';

import * as analytics from 'analytics/Analytics';
import {COLORS, ColorValueLink, PANES} from 'ui-library/ColorPickerConstants';
import lazyInit from 'util/lazyInit';

import withDropdownCloseControl, {DropdownCloseControlProps} from './menus/withDropdownCloseControl';

// Remember the last pane the user selected, to use across component mounts
const getLastSelectedPaneLink = lazyInit(() => {
  let lastSelectedPane = PANES.TEXT;
  return {
    getValue: () => lastSelectedPane,
    onChange: (newLastSelectedPane: string) => {
      lastSelectedPane = newLastSelectedPane;
    },
  };
});

export interface ColorPickerProps extends DropdownCloseControlProps {
  foregroundColorLink: ColorValueLink;
  backgroundColorLink: ColorValueLink;
  onRequestClose?: (...args: Array<any>) => any;
  /**
   * String that will be used as the foreground color name label.
   *
   * @default 'Text'
   */
  foregroundName?: string;
  /**
   * String that will be used as the background color name label.
   *
   * @default 'Highlight'
   */
  backgroundName?: string;
}

type State = any;

class ColorPicker extends React.Component<ColorPickerProps, State> {
  state: State = {
    selectedPane: getLastSelectedPaneLink().getValue(),
  };

  getShownSelectedColor = (): string | boolean | null | undefined => {
    if (this.state.selectedPane === PANES.TEXT) {
      return this.props.foregroundColorLink.value;
    }
    return this.props.backgroundColorLink.value;
  };

  _handleSelectedPaneChange = (newSelectedPane: any, e: any): void => {
    getLastSelectedPaneLink().onChange(newSelectedPane);
    this.setState({selectedPane: newSelectedPane});
    e.stopPropagation();
  };

  _handleTextPaneClick = (e: any): void => {
    this._handleSelectedPaneChange(PANES.TEXT, e);
  };

  _handleHighlightPaneClick = (e: any): void => {
    this._handleSelectedPaneChange(PANES.HIGHLIGHT, e);
  };

  _handleMouseDown = (e: any): void => {
    e.preventDefault(); // Prevents interaction with this component from changing the text selection
  };

  _handleSwatchClick = (e: any): void => {
    const colorValue = e.currentTarget.getAttribute('data-color');
    if (this.state.selectedPane === PANES.TEXT) {
      analytics.track('eln', 'changed_text_color', {color: colorValue});
      this.props.foregroundColorLink.requestChange(colorValue);
    } else {
      analytics.track('eln', 'changed_highlight_color', {color: colorValue});
      this.props.backgroundColorLink.requestChange(colorValue);
    }
    if (this.props.onRequestClose) {
      this.props.onRequestClose(e);
    }
    if (this.props._onCloseDropdown) {
      this.props._onCloseDropdown();
    }
  };

  createSwatchTd = (colorValue: any, extraClasses: string | null = null): JSX.Element => (
    <td key={colorValue} className="u-no-padding">
      <div
        data-color={colorValue}
        onClick={this._handleSwatchClick}
        className={classNames('colorPicker-swatch', extraClasses, {
          'colorPicker-swatch--selected': this.getShownSelectedColor() === colorValue,
        })}
        style={{backgroundColor: colorValue}}
      />
    </td>
  );

  render(): JSX.Element {
    // NB: The color picker header is wrapped in a `FORM` tag to allow the
    // header to be clicked without being dismissed when wrapped in a Bootstrap
    // dropdown.
    return (
      <div className="colorPicker" onMouseDown={this._handleMouseDown}>
        <form>
          <div className="colorPicker-headerContainer">
            <span
              id="colorPicker-foreground"
              className={classNames('colorPicker-header', {
                'colorPicker-header-selected': this.state.selectedPane === PANES.TEXT,
              })}
              onClick={this._handleTextPaneClick}
            >
              {this.props.foregroundName ?? 'Text'}
            </span>
            <span
              id="colorPicker-background"
              className={classNames('colorPicker-header u-small-spacing-left', {
                'colorPicker-header-selected': this.state.selectedPane === PANES.HIGHLIGHT,
              })}
              onClick={this._handleHighlightPaneClick}
            >
              {this.props.backgroundName ?? 'Highlight'}
            </span>
          </div>
          <div className="colorPicker-bodyContainer">
            <table className="colorPicker-table">
              <tbody>
                <tr>
                  {COLORS.GRAYS.map((colorValue) => this.createSwatchTd(colorValue))}
                  {this.createSwatchTd(COLORS.WHITE, 'colorPicker-swatch--bordered')}
                  {this.createSwatchTd(
                    COLORS.NO_STYLE,
                    'colorPicker-swatch--bordered colorPicker-swatch--empty'
                  )}
                </tr>
                <tr className="colorPicker-table-emptyRow" />
                {COLORS.PALETTE.map((colorRow, i) => (
                  <tr key={`row-${i}`}>{colorRow.map((colorValue) => this.createSwatchTd(colorValue))}</tr>
                ))}
              </tbody>
            </table>
          </div>
        </form>
      </div>
    );
  }
}

// Exporting the class directly for testing purposes
export {ColorPicker};

export default withDropdownCloseControl(ColorPicker);
